/**
 * Consent form specifically for the Information Sharing (news evaluation) study.
 * Separate from the phishing experiment consent — neutral language about
 * "evaluating news articles" with no mention of fake news, misinformation, etc.
 */

import React, { useState } from 'react';

interface InfoSharingConsentProps {
  onConsent: () => void;
}

const InfoSharingConsent: React.FC<InfoSharingConsentProps> = ({ onConsent }) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <div style={{ maxWidth: '700px', margin: '40px auto', padding: '2rem', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', textAlign: 'center' }}>Research Consent Form</h1>
      <h2 style={{ fontSize: '1.1rem', color: '#555', textAlign: 'center', marginBottom: '2rem', fontWeight: 400 }}>
        News Article Evaluation Study
      </h2>

      <div style={{ lineHeight: 1.8, fontSize: '0.95rem', color: '#333' }}>
        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Study Information</h3>
        <p>
          You are being invited to participate in a research study about how people evaluate and respond to
          news articles they encounter online. This study is conducted by researchers at a U.S. university
          and has been reviewed by the Institutional Review Board (IRB).
        </p>

        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>What You Will Do</h3>
        <ul>
          <li>Complete a brief background survey (2-3 minutes)</li>
          <li>Read four news articles and, for each, answer a set of rating questions about the article, its source, and its content, then indicate what you would do with it</li>
        </ul>
        <p><strong>Total time: Approximately 15 minutes</strong></p>

        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Risks</h3>
        <p>
          This study involves minimal risk. You may encounter news content on topics that you find
          politically or emotionally engaging. You may withdraw at any point; however, compensation is
          provided only at the end of the study.
        </p>

        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Benefits</h3>
        <p>
          Your participation will help improve understanding of how people process and evaluate
          information they encounter online.
        </p>

        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Compensation</h3>
        <p>
          You will be compensated through Prolific at the rate specified in the study listing
          upon successful completion of the study.
        </p>

        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Confidentiality</h3>
        <p>
          Your responses will be kept confidential and stored securely. Your Prolific ID will be
          used only for compensation purposes and will not be linked to your responses in any
          published results. All data will be reported in aggregate form only.
        </p>

        <h3 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Voluntary Participation</h3>
        <p>
          Participation is voluntary. You may withdraw at any time by closing the browser window.
          If you withdraw, your partial data will not be used.
        </p>
      </div>

      <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e1e5e9' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            style={{ marginTop: '4px', width: '18px', height: '18px' }}
          />
          <span style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
            I have read and understood the information above. I am 18 years of age or older.
            I voluntarily agree to participate in this study.
          </span>
        </label>
      </div>

      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <button
          onClick={onConsent}
          disabled={!agreed}
          style={{
            padding: '12px 48px', fontSize: '16px', fontWeight: 600,
            backgroundColor: agreed ? '#0078d4' : '#ccc',
            color: 'white', border: 'none', borderRadius: '6px',
            cursor: agreed ? 'pointer' : 'not-allowed',
          }}
        >
          I Agree — Begin Study
        </button>
      </div>
    </div>
  );
};

export default InfoSharingConsent;
